import { useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePopoutWindow } from '@/hooks/usePopoutWindow'
import { useScribeSession, type ScribeSeed } from '@/hooks/useScribeSession'
import { ScribePopout } from './ScribePopout'
import {
  COMPACT_SIZE,
  EXPANDED_SIZE,
  ScribePopoutContext,
  isExpandedPhase,
} from './scribePopoutContext'

/**
 * Owns the pop-out window and renders the scribe into it.
 *
 * Mounted once at the app root so the window survives navigation — a clinician
 * moving between patients mid-clinic must not lose the floating scribe. The
 * React tree inside the window is portalled from HERE, which is what lets it
 * read the same store and the same React Query cache as the main tab: one
 * note, two surfaces, no synchronisation to get wrong.
 */
export function ScribePopoutProvider({ children }: { children: React.ReactNode }) {
  const popout = usePopoutWindow()
  // The note the window was opened on. `key` bumps per open so reopening on a
  // note that was already dismissed shows it again instead of an empty window.
  const [seed, setSeed] = useState<ScribeSeed>({ noteId: null, key: 0 })
  const session = useScribeSession(popout.isOpen, seed)
  const expanded = isExpandedPhase(session.phase)

  // Resizing is driven by CLICKS, never by an effect. Measured 2026-08-19: a
  // document PiP window refuses resizeTo() without transient user activation,
  // so the obvious "grow when the note lands" effect throws every time and the
  // window stays stuck at its opening size — which is exactly what shipped.
  //
  // Stop is the click that does the work: the clinician presses it in this
  // window at the moment the consult ends and screen space becomes free, so
  // the window is already big by the time generation finishes. `expandNow`
  // covers the other path — a recording stopped from the main tab leaves no
  // gesture here, so the ready state offers it explicitly.
  const expandNow = useCallback(() => popout.resize(EXPANDED_SIZE), [popout])
  const shrinkNow = useCallback(() => popout.resize(COMPACT_SIZE), [popout])

  const capturing =
    session.phase === 'recording' || session.phase === 'paused' || session.phase === 'processing'

  const open = useCallback(
    (noteId?: number | null) => {
      setSeed((s) => ({ noteId: noteId ?? null, key: s.key + 1 }))
      // Opening ON a note means a note is about to be shown, so open at the
      // size that can show one — the window cannot be grown afterwards without
      // another click. Mid-consult the bar still wins: capture is what the
      // clinician needs on screen then, not text.
      const wantsNote = noteId != null && !capturing
      void popout.open(wantsNote || expanded ? EXPANDED_SIZE : COMPACT_SIZE)
    },
    [popout, expanded, capturing],
  )

  const api = useMemo(
    () => ({
      isOpen: popout.isOpen,
      alwaysOnTop: popout.supported,
      open,
      close: popout.close,
    }),
    [popout.isOpen, popout.supported, popout.close, open],
  )

  return (
    <ScribePopoutContext.Provider value={api}>
      {children}
      {popout.container &&
        createPortal(
          <ScribePopout
            session={session}
            onExpand={expandNow}
            onShrink={shrinkNow}
            onCollapse={session.dismiss}
          />,
          popout.container,
        )}
    </ScribePopoutContext.Provider>
  )
}
